// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import ChoiceCard from "src/pages/EmrConnections/ChoiceCard/ChoiceCard";
import InfoNote from "src/pages/EmrConnections/InfoNote/InfoNote";
import ExamTypePicker from "src/pages/EmrConnections/ExamTypePicker/ExamTypePicker";
import WorklistTiming from "src/pages/EmrConnections/WorklistTiming/WorklistTiming";
import { CopyIcon } from "src/assets/icons";
import Walkthrough from "../Walkthrough/Walkthrough";
import {
  MockButton,
  MockCheckbox,
  MockDialog,
  MockField,
  MockMoreOptions,
  MockPage,
  MockQuestion,
  MockRow,
  MockSelect,
  MockSnack,
  MockTogglePanel,
  noop,
} from "../GuideMocks/PortalMocks";
import {
  MockCardGrid,
  MockConnectionsHeader,
  MockHubSection,
  MockOutboxPanel,
  MockSystemCard,
} from "../GuideMocks/ConnectionMocks";
import { GUIDE_NAMESPACE } from "../GuideText/GuideText";

// Utils
import { WalkthroughStep, guideTarget } from "../Walkthrough/walkthroughState";

const PAC_SUBTITLE = "DICOM · 10.0.0.20 : 104 · AE FORUM";

const useLabels = () => {
  const { t } = useTranslation();
  const { t: tg } = useTranslation(GUIDE_NAMESPACE);

  return { t, tg };
};

const WizardFooter = ({
  first = false,
  action,
  actionTarget,
}: {
  first?: boolean;
  action: string;
  actionTarget?: string;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <MockButton kind="cancel">{first ? t("Cancel") : t("Back")}</MockButton>
      <MockButton kind="confirm" targetId={actionTarget}>
        {action}
      </MockButton>
    </>
  );
};

const OVERVIEW_STEPS: Array<WalkthroughStep> = [
  { target: "ptitle", text: "This is the Connections page of one hub." },
  {
    target: "sec-wl",
    text: "<b>Worklist</b>: where today's bookings come from.",
  },
  {
    target: "sec-mc",
    text: "<b>Machine</b>: devices that send exams straight to the hub.",
  },
  {
    target: "sec-rx",
    text: "<b>RetinaLogik Exams</b>: where finished reports go back to.",
  },
  {
    target: "add-wl",
    text: "Each group has its own teal <b>Add</b> button to start a short guided setup.",
  },
  {
    target: "back",
    text: "<b>Back to Data Hub</b> returns to your list of hubs.",
  },
];

export const ConnectionsWalkthrough = () => {
  const { tg } = useLabels();

  return (
    <Walkthrough id="connections" steps={OVERVIEW_STEPS}>
      {() => (
        <MockPage>
          <MockConnectionsHeader
            title={tg("Front desk PC")}
            backTarget="back"
            titleTarget="ptitle"
          />
          <MockHubSection
            kind="worklist"
            sectionTarget="sec-wl"
            addTarget="add-wl"
          />
          <MockHubSection kind="machine" sectionTarget="sec-mc" />
          <MockHubSection kind="reports" sectionTarget="sec-rx" />
        </MockPage>
      )}
    </Walkthrough>
  );
};

const WORKLIST_PAC_STEPS: Array<WalkthroughStep> = [
  {
    screen: "sec",
    target: "add",
    text: "In the <b>Worklist</b> group, click <b>Add worklist</b>.",
  },
  {
    screen: "s1",
    target: "c-pac",
    text: "The question is where your schedule comes from. Choose <b>PAC server</b>.",
  },
  { on: ["pac"], target: "next1", text: "Click <b>Next</b>." },
  {
    screen: "s2",
    target: "f-name",
    on: ["name"],
    text: "Give the server a name everyone recognises, like <b>FORUM</b>.",
  },
  {
    target: "f-host",
    on: ["host"],
    text: "<b>Host / IP</b> and <b>Port</b>: the server's address. IT or the archive vendor gives you these. Port is usually 104.",
  },
  {
    target: "f-ae",
    on: ["ae"],
    text: "<b>PAC server AE title</b>: the server's DICOM name, also from IT. Leave <b>Hub AE title</b> as EMRHUB unless told otherwise.",
  },
  {
    target: "f-timing",
    text: "<b>How often</b>: keep <b>Keep it up to date</b>. The hub then rereads the schedule every 3 minutes.",
  },
  {
    target: "f-station",
    text: "<b>Scheduled station AE title</b> is optional. Leave it empty unless IT asks you to fill it.",
  },
  { target: "next2", text: "Click <b>Next</b>." },
  {
    screen: "s3",
    target: "q-toggle",
    on: ["q"],
    text: "Optional but useful: switch on <b>Bring in their exams</b>. The hub then fetches the booked patients' exams from the server.",
  },
  { target: "q-types", text: "Tick the kinds of exams you want." },
  {
    target: "q-prior",
    text: "Choose how many older exams to bring the first time a patient is booked. <b>The last 2 years</b> is the usual choice.",
  },
  { target: "create", text: "Click <b>Create worklist</b>." },
  {
    screen: "res",
    target: "card",
    text: "The PAC server now has its own card, with a <b>Worklist</b> job and a <b>Query</b> job.",
  },
  {
    target: "pill",
    text: "<b>Pending</b> turns into <b>Working</b> once the hub has really reached it, usually within a minute. A typo in an address shows <b>Failing</b> instead.",
  },
];

const TIMING_INTERVAL = {
  mode: "Interval" as const,
  pollIntervalMinutes: 3,
  importTimes: ["06:30", "12:00"],
  lookaheadDays: "0",
};

export const WorklistPacWalkthrough = () => {
  const { t } = useLabels();
  const steps = [t("Source"), t("PAC server"), t("Query (optional)")];

  return (
    <Walkthrough id="worklist-pac" steps={WORKLIST_PAC_STEPS}>
      {(view) => (
        <>
          {view.screen === "sec" && (
            <MockHubSection kind="worklist" addTarget="add" />
          )}
          {view.screen === "s1" && (
            <MockDialog
              title={t("Add a worklist")}
              steps={steps}
              current={0}
              footer={
                <WizardFooter first action={t("Next")} actionTarget="next1" />
              }
            >
              <MockQuestion
                title={t("Where does the clinic's schedule come from?")}
              />
              <Box {...guideTarget("c-pac")}>
                <ChoiceCard
                  name="guide-worklist-pac-source"
                  value="PacServer"
                  checked={view.isOn("pac")}
                  title={t("PAC server")}
                  description={t(
                    "The hub asks a DICOM PAC server or RIS for today's appointments, and can then query it for those patients' exams."
                  )}
                  onSelect={noop}
                />
              </Box>
              <ChoiceCard
                name="guide-worklist-pac-source"
                value="SharedFolder"
                checked={false}
                title={t("Shared folder")}
                description={t(
                  "Your EMR exports a CSV to a folder and the hub reads it at set times."
                )}
                onSelect={noop}
              />
            </MockDialog>
          )}
          {view.screen === "s2" && (
            <MockDialog
              title={t("Add a worklist")}
              steps={steps}
              current={1}
              footer={<WizardFooter action={t("Next")} actionTarget="next2" />}
            >
              <MockField
                label={t("Name")}
                required
                placeholder={t("e.g. Main PAC server")}
                value="FORUM"
                filled={view.isOn("name")}
                targetId="f-name"
              />
              <Box {...guideTarget("f-host")}>
                <MockRow>
                  <MockField
                    label={t("Host / IP")}
                    required
                    placeholder="10.0.0.20"
                    value="10.0.0.20"
                    filled={view.isOn("host")}
                  />
                  <MockField label={t("Port")} required value="104" />
                </MockRow>
              </Box>
              <Box {...guideTarget("f-ae")}>
                <MockRow>
                  <MockField
                    label={t("PAC server AE title")}
                    required
                    placeholder="PACS"
                    value="FORUM"
                    filled={view.isOn("ae")}
                  />
                  <MockField
                    label={t("Hub AE title")}
                    required
                    value="EMRHUB"
                  />
                </MockRow>
              </Box>
              <Typography fontSize="14px" fontWeight={700}>
                {t("How often")}
              </Typography>
              <Box {...guideTarget("f-timing")}>
                <WorklistTiming
                  idPrefix="guide-worklist-pac"
                  value={TIMING_INTERVAL}
                  onChange={noop}
                />
              </Box>
              <MockField
                label={t("Scheduled station AE title")}
                placeholder={t("Optional")}
                value=""
                filled={false}
                targetId="f-station"
              />
            </MockDialog>
          )}
          {view.screen === "s3" && (
            <MockDialog
              title={t("Add a worklist")}
              steps={steps}
              current={2}
              footer={
                <WizardFooter
                  action={t("Create worklist")}
                  actionTarget="create"
                />
              }
            >
              <MockQuestion
                title={t("Query the PAC server for these patients' exams?")}
                subtitle={t(
                  "Optional. The address is the worklist's; change it under More options if exams live elsewhere."
                )}
              />
              <MockTogglePanel
                title={t("Bring in their exams")}
                description={t(
                  "Only for patients on the worklist. Nobody else's exams leave {{name}}.",
                  { name: "FORUM" }
                )}
                isOn={view.isOn("q")}
                targetId="q-toggle"
              >
                <Box {...guideTarget("q-types")}>
                  <ExamTypePicker
                    options={[
                      "Oct",
                      "Fundus",
                      "AnteriorSegment",
                      "VisualField",
                      "Other",
                    ]}
                    value={["Oct", "Fundus", "VisualField"]}
                    onChange={noop}
                  />
                </Box>
                <Typography {...guideTarget("q-prior")} fontSize="13px">
                  {t("The first time a patient is booked, also bring")}{" "}
                  <b>{t("the last 2 years")}</b>
                </Typography>
                <MockMoreOptions />
              </MockTogglePanel>
            </MockDialog>
          )}
          {view.screen === "res" && (
            <>
              <MockHubSection kind="worklist">
                <MockCardGrid>
                  <MockSystemCard
                    icon="server"
                    name="FORUM"
                    subtitle={PAC_SUBTITLE}
                    canDelete={false}
                    cardTarget="card"
                    jobs={[
                      {
                        direction: "In",
                        title: t("Worklist"),
                        description: `${t("Read every {{minutes}} min", { minutes: 30 })} · ${t("today's appointments")}`,
                        status: "Pending",
                        statusTarget: "pill",
                      },
                      {
                        direction: "In",
                        title: t("Query"),
                        description: `${t("Only for patients on the worklist")} · OCT, Fundus, ${t("Visual Field")} · ${t("{{period}} for a new patient", { period: t("the last 2 years") })}`,
                        status: "Pending",
                      },
                    ]}
                  />
                </MockCardGrid>
              </MockHubSection>
              <Box mt="12px">
                <MockSnack text={t("PAC server created successfully!")} />
              </Box>
            </>
          )}
        </>
      )}
    </Walkthrough>
  );
};

const WORKLIST_CSV_STEPS: Array<WalkthroughStep> = [
  {
    screen: "s1",
    target: "c-sf",
    text: "After <b>Add worklist</b>, choose <b>Shared folder</b>.",
  },
  { on: ["sf"], target: "next1", text: "Click <b>Next</b>." },
  {
    screen: "s2",
    target: "f-folder",
    on: ["folder"],
    text: "Type the folder where your EMR saves the schedule. IT can tell you the path.",
  },
  {
    target: "f-name",
    on: ["name"],
    text: "Give it a name, like <b>EMR worklist</b>.",
  },
  { target: "next2", text: "Click <b>Next</b>." },
  {
    screen: "s3",
    target: "t-keep",
    text: "<b>Keep it up to date</b> rereads the file every 3, 5, 15, 30, 60 or 120 minutes. Best if your EMR updates the file during the day.",
  },
  {
    target: "t-set",
    on: ["set"],
    text: "Or pick <b>At set times</b> if your EMR saves the file only at certain times.",
  },
  {
    target: "t-times",
    text: "Remove a time with the x, or click <b>Add time</b>. Times are the hub PC's local time.",
  },
  {
    target: "t-sum",
    text: "The summary repeats your choice in one sentence. Read it to double-check.",
  },
  { target: "create", text: "Click <b>Create worklist</b>." },
  {
    screen: "res",
    target: "card",
    text: "The folder appears in the Worklist group, with its schedule written out.",
  },
];

export const WorklistCsvWalkthrough = () => {
  const { t, tg } = useLabels();
  const steps = [t("Source"), t("Folder"), t("Worklist timing")];
  const folder = "\\\\emr01\\worklist";

  return (
    <Walkthrough id="worklist-csv" steps={WORKLIST_CSV_STEPS}>
      {(view) => {
        const timing = view.isOn("set")
          ? { ...TIMING_INTERVAL, mode: "Times" as const }
          : TIMING_INTERVAL;
        const timingText = view.isOn("set")
          ? t("Read at {{times}}", { times: "06:30, 12:00" })
          : t("Read every {{minutes}} min", { minutes: 30 });

        return (
          <>
            {view.screen === "s1" && (
              <MockDialog
                title={t("Add a worklist")}
                steps={steps}
                current={0}
                footer={
                  <WizardFooter first action={t("Next")} actionTarget="next1" />
                }
              >
                <MockQuestion
                  title={t("Where does the clinic's schedule come from?")}
                />
                <ChoiceCard
                  name="guide-worklist-csv-source"
                  value="PacServer"
                  checked={false}
                  title={t("PAC server")}
                  description={t(
                    "The hub asks a DICOM PAC server or RIS for today's appointments, and can then query it for those patients' exams."
                  )}
                  onSelect={noop}
                />
                <Box {...guideTarget("c-sf")}>
                  <ChoiceCard
                    name="guide-worklist-csv-source"
                    value="SharedFolder"
                    checked={view.isOn("sf")}
                    title={t("Shared folder")}
                    description={t(
                      "Your EMR exports a CSV to a folder and the hub reads it at set times."
                    )}
                    onSelect={noop}
                  />
                </Box>
              </MockDialog>
            )}
            {view.screen === "s2" && (
              <MockDialog
                title={t("Add a worklist")}
                steps={steps}
                current={1}
                footer={
                  <WizardFooter action={t("Next")} actionTarget="next2" />
                }
              >
                <MockField
                  label={t("Folder")}
                  required
                  placeholder={folder}
                  value={folder}
                  filled={view.isOn("folder")}
                  helper={t(
                    "A local folder on the hub PC or a network share. The hub reads it with its own Windows account."
                  )}
                  targetId="f-folder"
                />
                <MockField
                  label={t("Name")}
                  required
                  placeholder={t("e.g. EMR worklist")}
                  value={tg("EMR worklist")}
                  filled={view.isOn("name")}
                  targetId="f-name"
                />
              </MockDialog>
            )}
            {view.screen === "s3" && (
              <MockDialog
                title={t("Add a worklist")}
                steps={steps}
                current={2}
                footer={
                  <WizardFooter
                    action={t("Create worklist")}
                    actionTarget="create"
                  />
                }
              >
                <MockQuestion
                  title={t("When should the worklist be read?")}
                  subtitle={t(
                    "PAC server exams are fetched only for patients on the worklist, so keeping it current means same-day bookings get their exams too."
                  )}
                />
                <Box {...guideTarget("t-keep", "label:nth-of-type(1)")}>
                  <Box {...guideTarget("t-set", "label:nth-of-type(2)")}>
                    <Box {...guideTarget("t-times", "label:nth-of-type(2)")}>
                      <WorklistTiming
                        idPrefix="guide-worklist-csv"
                        value={timing}
                        onChange={noop}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box {...guideTarget("t-sum")}>
                  <InfoNote>
                    {t(
                      "The hub reads today's worklist file from {{path}}: {{timing}}.",
                      {
                        path: folder,
                        timing: timingText.toLowerCase(),
                      }
                    )}
                  </InfoNote>
                </Box>
              </MockDialog>
            )}
            {view.screen === "res" && (
              <MockHubSection kind="worklist">
                <MockCardGrid>
                  <MockSystemCard
                    icon="folder"
                    name={tg("EMR worklist")}
                    subtitle={folder}
                    cardTarget="card"
                    jobs={[
                      {
                        direction: "In",
                        title: t("Worklist"),
                        description: `${t("Worklist CSV")} · ${t("Read at {{times}}", { times: "06:30, 12:00" })} · ${t("today's appointments")}`,
                        status: "Working",
                      },
                    ]}
                  />
                </MockCardGrid>
              </MockHubSection>
            )}
          </>
        );
      }}
    </Walkthrough>
  );
};

const MACHINE_STEPS_LABELS = ["Machine", "Exams", "Details", "Review"];

const MACHINE_DICOM_STEPS: Array<WalkthroughStep> = [
  {
    screen: "sec",
    target: "add",
    text: "In the <b>Machine</b> group, click <b>Add machine</b>.",
  },
  {
    screen: "m1",
    target: "m-model",
    on: ["mk"],
    show: ["sum"],
    text: "Pick the machine's <b>Manufacturer</b> and <b>Model</b>. Only supported machines are listed.",
  },
  {
    target: "m-sum",
    text: "The portal says what this model makes and how it can send exams.",
  },
  {
    target: "m-name",
    text: "The <b>Name</b> fills in by itself. Add the room so everyone knows which machine it is.",
  },
  { target: "next", text: "Click <b>Next</b>." },
  {
    screen: "m2",
    target: "m-types",
    text: "Tick the exams to bring in. Only what this model makes is offered.",
  },
  {
    target: "m-send",
    text: "Choose <b>It sends them to the hub</b>. This is the usual way when the machine supports DICOM.",
  },
  { target: "next", text: "Click <b>Next</b>." },
  {
    screen: "m3",
    target: "m-ae",
    on: ["ae"],
    text: "<b>Machine AE title</b>: the machine's own DICOM name. The technician finds it in the machine's settings. Only this name will be let in.",
  },
  {
    target: "m-hub",
    text: "<b>The hub listens as</b>: the portal fills these in for you. Keep them.",
  },
  {
    target: "m-box",
    text: "This box is what the technician enters on the machine.",
  },
  {
    target: "m-copy",
    show: ["copied"],
    text: "Click <b>Copy for the technician</b> and paste it into an email or message to them.",
  },
  { target: "next", text: "Click <b>Next</b>." },
  {
    screen: "m4",
    target: "m-review",
    text: "Read the summary sentence. If something is wrong, click <b>Back</b>.",
  },
  { target: "create", text: "Click <b>Create connection</b>." },
  {
    screen: "res",
    target: "card",
    text: "The machine appears in the Machine group. Once the technician has set up the machine, send a test exam and check it shows <b>Working</b>.",
  },
];

const MachineSendChoices = ({
  folderChecked,
  sendTarget,
  folderTarget,
}: {
  folderChecked: boolean;
  sendTarget?: string;
  folderTarget?: string;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <MockQuestion title={t("How does the machine send them?")} />
      <Box {...(sendTarget ? guideTarget(sendTarget) : {})}>
        <ChoiceCard
          name="guide-machine-send"
          value="Dicom"
          checked={!folderChecked}
          title={t("It sends them to the hub")}
          badge={t("Recommended")}
          description={t(
            "DICOM. You point the machine at the hub; the next step shows the values to enter."
          )}
          onSelect={noop}
        />
      </Box>
      <Box {...(folderTarget ? guideTarget(folderTarget) : {})}>
        <ChoiceCard
          name="guide-machine-send"
          value="Folder"
          checked={folderChecked}
          title={t("It saves files to a folder")}
          description={t(
            "PDF or DICOM export to a folder the hub watches. The exam type is read from each file."
          )}
          onSelect={noop}
        />
      </Box>
    </>
  );
};

const KeyValueBox = ({ rows }: { rows: Array<[string, string]> }) => (
  <Box
    sx={{
      display: "grid",
      gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "150px minmax(0, 1fr)" },
      gap: "4px 10px",
      fontSize: "13px",
    }}
  >
    {rows.map(([key, value]) => (
      <Box key={key} display="contents">
        <span>{key}</span>
        <Box component="b" fontFamily="Consolas, 'Courier New', monospace">
          {value}
        </Box>
      </Box>
    ))}
  </Box>
);

export const MachineDicomWalkthrough = () => {
  const { t, tg } = useLabels();
  const steps = MACHINE_STEPS_LABELS.map((label) => t(label));

  return (
    <Walkthrough id="machine-dicom" steps={MACHINE_DICOM_STEPS}>
      {(view) => (
        <>
          {view.screen === "sec" && (
            <MockHubSection kind="machine" addTarget="add" />
          )}
          {view.screen === "m1" && (
            <MockDialog
              title={t("Add a machine")}
              steps={steps}
              current={0}
              footer={
                <WizardFooter first action={t("Next")} actionTarget="next" />
              }
            >
              <InfoNote>
                {t(
                  "A machine that only sends to the PAC server needs nothing here: its exams come in through the worklist's query."
                )}
              </InfoNote>
              <Box {...guideTarget("m-model")}>
                <MockRow>
                  <MockSelect
                    label={t("Manufacturer")}
                    required
                    placeholder={t("Select a value")}
                    value="Zeiss"
                    filled={view.isOn("mk")}
                  />
                  <MockSelect
                    label={t("Model")}
                    required
                    placeholder={t("Select a value")}
                    value="Cirrus 5000"
                    filled={view.isOn("mk")}
                  />
                </MockRow>
              </Box>
              {view.shows("sum") && (
                <Box {...guideTarget("m-sum")}>
                  <InfoNote>
                    {`${t("{{device}} makes {{examTypes}}", { device: "Cirrus 5000", examTypes: "OCT" })} · ${t("Can send over DICOM")} · ${t("Can export files to a folder")}`}
                  </InfoNote>
                </Box>
              )}
              <MockField
                label={t("Name")}
                required
                placeholder={t("e.g. Cirrus, room 2")}
                value={tg("Cirrus, room 2")}
                filled={view.isOn("mk")}
                targetId="m-name"
              />
            </MockDialog>
          )}
          {view.screen === "m2" && (
            <MockDialog
              title={t("Add a machine")}
              steps={steps}
              current={1}
              footer={<WizardFooter action={t("Next")} actionTarget="next" />}
            >
              <MockQuestion
                title={t("Which exams should it bring in?")}
                subtitle={t(
                  "Only what this model makes is listed. The hub turns anything else away."
                )}
              />
              <Box {...guideTarget("m-types")} alignSelf="flex-start">
                <ExamTypePicker
                  options={["Oct"]}
                  value={["Oct"]}
                  onChange={noop}
                />
              </Box>
              <MachineSendChoices folderChecked={false} sendTarget="m-send" />
            </MockDialog>
          )}
          {view.screen === "m3" && (
            <MockDialog
              title={t("Add a machine")}
              steps={steps}
              current={2}
              footer={<WizardFooter action={t("Next")} actionTarget="next" />}
            >
              <MockQuestion title={t("Who may send")} />
              <Box {...guideTarget("m-ae")}>
                <MockRow>
                  <MockField
                    label={t("Machine AE title")}
                    required
                    placeholder={t("e.g. CIRRUS01")}
                    value="CIRRUS01"
                    filled={view.isOn("ae")}
                  />
                  <MockField
                    label={t("Machine IP address")}
                    placeholder={t("Optional, e.g. 10.0.0.25")}
                    value=""
                    filled={false}
                  />
                </MockRow>
              </Box>
              <InfoNote>
                {t(
                  "The name the machine uses for itself on the network. Anything calling with a different name is turned away."
                )}
              </InfoNote>
              <MockQuestion title={t("The hub listens as")} />
              <Box {...guideTarget("m-hub")}>
                <MockRow>
                  <MockField
                    label={t("Hub AE title")}
                    required
                    value="EMRHUB"
                  />
                  <MockField label={t("Hub port")} required value="104" />
                </MockRow>
              </Box>
              <Box
                {...guideTarget("m-box")}
                sx={{
                  border: "1.5px dashed #5B6D7A",
                  borderRadius: "8px",
                  padding: "12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                }}
              >
                <Typography fontSize="13px" fontWeight={700}>
                  {t("Enter this on the machine as its DICOM destination")}
                </Typography>
                <KeyValueBox
                  rows={[
                    [t("Host"), "10.0.0.15"],
                    [t("Port"), "104"],
                    [t("Called AE title"), "EMRHUB"],
                    [t("Calling AE title"), "CIRRUS01"],
                  ]}
                />
                <Box alignSelf="flex-start">
                  <MockButton
                    kind="cancel"
                    small
                    startIcon={<CopyIcon width="16px" height="16px" />}
                    targetId="m-copy"
                  >
                    {view.shows("copied")
                      ? t("Copied")
                      : t("Copy for the technician")}
                  </MockButton>
                </Box>
              </Box>
            </MockDialog>
          )}
          {view.screen === "m4" && (
            <MockDialog
              title={t("Add a machine")}
              steps={steps}
              current={3}
              footer={
                <WizardFooter
                  action={t("Create connection")}
                  actionTarget="create"
                />
              }
            >
              <Box {...guideTarget("m-review")}>
                <InfoNote>
                  {t(
                    "{{name}} sends {{examTypes}} exams from a {{device}} straight to the hub over DICOM, as {{aeTitle}} : {{port}}, accepting only {{callingAeTitle}}.",
                    {
                      name: tg("Cirrus, room 2"),
                      examTypes: "OCT",
                      device: "Zeiss Cirrus 5000",
                      aeTitle: "EMRHUB",
                      port: "104",
                      callingAeTitle: "CIRRUS01",
                    }
                  )}
                </InfoNote>
              </Box>
            </MockDialog>
          )}
          {view.screen === "res" && (
            <MockHubSection kind="machine">
              <MockCardGrid>
                <MockSystemCard
                  icon="machine"
                  name={tg("Cirrus, room 2")}
                  subtitle="Zeiss Cirrus 5000"
                  cardTarget="card"
                  jobs={[
                    {
                      direction: "In",
                      title: t("Exams"),
                      description: `OCT · ${t("sends over DICOM")}`,
                      status: "Working",
                    },
                  ]}
                />
              </MockCardGrid>
            </MockHubSection>
          )}
        </>
      )}
    </Walkthrough>
  );
};

const MACHINE_FOLDER_STEPS: Array<WalkthroughStep> = [
  {
    screen: "m2",
    target: "c-folder",
    text: "Steps 1 and 2 are the same as above. In step 2, choose <b>It saves files to a folder</b>.",
  },
  { on: ["folder"], target: "next", text: "Click <b>Next</b>." },
  {
    screen: "m3",
    target: "f-folder",
    on: ["path"],
    text: "Type the folder the machine exports to. It can be on the hub PC or a shared network folder.",
  },
  { target: "next", text: "Click <b>Next</b>." },
  { screen: "m4", target: "m-review", text: "Check the summary." },
  { target: "create", text: "Click <b>Create connection</b>." },
  {
    screen: "res",
    target: "card",
    text: "The hub now watches that folder and picks up each new file.",
  },
];

export const MachineFolderWalkthrough = () => {
  const { t, tg } = useLabels();
  const steps = MACHINE_STEPS_LABELS.map((label) => t(label));
  const folder = "\\\\frontdesk\\exports\\hfa";

  return (
    <Walkthrough id="machine-folder" steps={MACHINE_FOLDER_STEPS}>
      {(view) => (
        <>
          {view.screen === "m2" && (
            <MockDialog
              title={t("Add a machine")}
              steps={steps}
              current={1}
              footer={<WizardFooter action={t("Next")} actionTarget="next" />}
            >
              <MachineSendChoices
                folderChecked={view.isOn("folder")}
                folderTarget="c-folder"
              />
            </MockDialog>
          )}
          {view.screen === "m3" && (
            <MockDialog
              title={t("Add a machine")}
              steps={steps}
              current={2}
              footer={<WizardFooter action={t("Next")} actionTarget="next" />}
            >
              <MockField
                label={t("Folder")}
                required
                placeholder="\\server\share\exports"
                value={folder}
                filled={view.isOn("path")}
                helper={t(
                  "A local folder on the hub PC or a network share. The hub reads it with its own Windows account."
                )}
                targetId="f-folder"
              />
            </MockDialog>
          )}
          {view.screen === "m4" && (
            <MockDialog
              title={t("Add a machine")}
              steps={steps}
              current={3}
              footer={
                <WizardFooter
                  action={t("Create connection")}
                  actionTarget="create"
                />
              }
            >
              <Box {...guideTarget("m-review")}>
                <InfoNote>
                  {t(
                    "{{name}}: exams from a {{device}} are picked up from {{path}}.",
                    {
                      name: tg("HFA, room 1"),
                      device: "Zeiss HFA3",
                      path: folder,
                    }
                  )}
                </InfoNote>
              </Box>
            </MockDialog>
          )}
          {view.screen === "res" && (
            <MockHubSection kind="machine">
              <MockCardGrid>
                <MockSystemCard
                  icon="folder"
                  name={tg("HFA, room 1")}
                  subtitle={folder}
                  cardTarget="card"
                  jobs={[
                    {
                      direction: "In",
                      title: t("Exams"),
                      description: t("Files from {{path}}", { path: folder }),
                      status: "Working",
                    },
                  ]}
                />
              </MockCardGrid>
            </MockHubSection>
          )}
        </>
      )}
    </Walkthrough>
  );
};

const REPORTS_STEPS: Array<WalkthroughStep> = [
  {
    screen: "d1",
    target: "c-pac",
    on: ["pac"],
    text: "Choose where reports should go. For your archive, choose <b>PAC server</b>.",
  },
  { target: "next", text: "Click <b>Next</b>." },
  {
    screen: "d2",
    target: "p-sel",
    text: "If the PAC server is already set up for the worklist, just pick it. Otherwise pick <b>New DICOM PAC server</b> and type its address.",
  },
  {
    target: "p-date",
    text: "<b>Send reports of exams from</b>: today is the usual choice. Older exams are never sent.",
  },
  { target: "start", text: "Click <b>Start sending</b>." },
  {
    screen: "res",
    target: "card",
    text: "The <b>Reports</b> job appears. Its arrow points up: reports go out to your clinic.",
  },
  {
    target: "outbox",
    text: "The <b>Report Outbox</b> counts reports waiting, sent and failed.",
  },
  {
    screen: "d1",
    target: "c-sf",
    off: ["pac"],
    on: ["sf"],
    text: "For a folder instead, go back to step 1 and choose <b>Shared folder</b>.",
  },
  {
    screen: "d2f",
    target: "sf-folder",
    text: "Give it a name and type the folder your EMR imports reports from.",
  },
  {
    target: "sf-fmt",
    text: "Choose <b>PDF</b>, <b>DICOM</b>, or both. Ask your EMR vendor which one they import. PDF is the most common.",
  },
  { target: "start-f", text: "Click <b>Start sending</b>." },
];

export const ReportsWalkthrough = () => {
  const { t, tg } = useLabels();

  return (
    <Walkthrough id="reports" steps={REPORTS_STEPS}>
      {(view) => (
        <>
          {view.screen === "d1" && (
            <MockDialog
              title={t("Send RetinaLogik Exams")}
              steps={[t("Destination"), t("PAC server")]}
              current={0}
              footer={
                <WizardFooter first action={t("Next")} actionTarget="next" />
              }
            >
              <MockQuestion title={t("Where should finished reports go?")} />
              <Box {...guideTarget("c-pac")}>
                <ChoiceCard
                  name="guide-reports-destination"
                  value="PacServer"
                  checked={view.isOn("pac")}
                  title={t("PAC server")}
                  description={t(
                    "Stored in the clinic's DICOM PAC server, next to the original exam."
                  )}
                  onSelect={noop}
                />
              </Box>
              <Box {...guideTarget("c-sf")}>
                <ChoiceCard
                  name="guide-reports-destination"
                  value="SharedFolder"
                  checked={view.isOn("sf")}
                  title={t("Shared folder")}
                  description={t(
                    "The hub writes each report into a folder, for an EMR that imports from there."
                  )}
                  onSelect={noop}
                />
              </Box>
            </MockDialog>
          )}
          {view.screen === "d2" && (
            <MockDialog
              title={t("Send RetinaLogik Exams")}
              steps={[t("Destination"), t("PAC server")]}
              current={1}
              footer={
                <WizardFooter
                  action={t("Start sending")}
                  actionTarget="start"
                />
              }
            >
              <MockSelect
                label={t("PAC server")}
                required
                value="FORUM"
                targetId="p-sel"
              />
              <InfoNote>
                {t("Reports go to the address {{name}} already uses.", {
                  name: "FORUM",
                })}
              </InfoNote>
              <MockField
                label={t("Send reports of exams from")}
                required
                value="2026-10-19"
                targetId="p-date"
              />
              <InfoNote>
                {t(
                  "Reports of earlier exams are never sent, so the PAC server is not flooded with old reports."
                )}
              </InfoNote>
            </MockDialog>
          )}
          {view.screen === "d2f" && (
            <MockDialog
              title={t("Send RetinaLogik Exams")}
              steps={[t("Destination"), t("Shared folder")]}
              current={1}
              footer={
                <WizardFooter
                  action={t("Start sending")}
                  actionTarget="start-f"
                />
              }
            >
              <MockField
                label={t("Name")}
                required
                value={tg("EMR reports folder")}
              />
              <MockField
                label={t("Folder")}
                required
                value="\\emr01\retinalogik-reports"
                helper={t(
                  "A local folder on the hub PC or a network share. The hub writes there with its own Windows account."
                )}
                targetId="sf-folder"
              />
              <Box
                {...guideTarget("sf-fmt")}
                display="flex"
                flexDirection="column"
                gap="6px"
              >
                <Typography fontSize="13px">{t("Write reports as")}</Typography>
                <MockCheckbox label={t("PDF, one file per report")} checked />
                <MockCheckbox
                  label={t("DICOM, one file per report section")}
                  checked={false}
                />
              </Box>
              <MockField
                label={t("Send reports of exams from")}
                required
                value="2026-10-19"
              />
            </MockDialog>
          )}
          {view.screen === "res" && (
            <MockHubSection kind="reports">
              <MockCardGrid>
                <MockSystemCard
                  icon="server"
                  name="FORUM"
                  subtitle={PAC_SUBTITLE}
                  canDelete={false}
                  cardTarget="card"
                  jobs={[
                    {
                      direction: "Out",
                      title: t("Reports"),
                      description: `${t("RetinaLogik reports stored back")} · ${t("exams from {{date}} on", { date: "2026-10-19" })}`,
                      status: "Working",
                    },
                  ]}
                >
                  <MockOutboxPanel
                    pending={1}
                    sent={14}
                    failed={0}
                    panelTarget="outbox"
                  />
                </MockSystemCard>
              </MockCardGrid>
            </MockHubSection>
          )}
        </>
      )}
    </Walkthrough>
  );
};

const EDIT_PAC_STEPS: Array<WalkthroughStep> = [
  {
    target: "addr",
    text: "The server's address. Change it here and every job using it is updated.",
  },
  {
    target: "j-wl",
    text: "<b>Read the worklist</b>: switch on or off, and set how often.",
  },
  {
    target: "j-ex",
    text: "<b>Bring in their exams</b>: switch on or off, and pick exam types.",
  },
  {
    target: "j-more",
    text: "<b>More options</b> holds less common settings, like a different address for this job only.",
  },
  {
    target: "j-push",
    on: ["push"],
    text: "Only if IT says so: tick <b>Let FORUM push exams instead</b> when the archive sends exams to the hub by itself.",
  },
  {
    target: "j-rep",
    on: ["rep"],
    text: "<b>Send RetinaLogik reports back</b>: switch it on to store reports in this server.",
  },
  {
    target: "remove",
    text: "<b>Remove PAC server</b> stops all three jobs. Exams already received stay.",
  },
  { target: "save", text: "Click <b>Save changes</b>." },
];

export const EditPacWalkthrough = () => {
  const { t } = useLabels();

  return (
    <Walkthrough id="edit-pac" steps={EDIT_PAC_STEPS}>
      {(view) => (
        <MockDialog
          title={`${t("Edit")} FORUM`}
          footer={
            <>
              <MockButton kind="cancel">{t("Cancel")}</MockButton>
              <MockButton kind="confirm" targetId="save">
                {t("Save changes")}
              </MockButton>
            </>
          }
        >
          <Typography fontSize="14px" fontWeight={700}>
            {t("PAC server address")}
          </Typography>
          <Box {...guideTarget("addr")}>
            <MockRow>
              <MockField label={t("Host / IP")} required value="10.0.0.20" />
              <MockField label={t("Port")} required value="104" />
              <MockField label={t("Remote AE title")} required value="FORUM" />
              <MockField label={t("Hub AE title")} required value="EMRHUB" />
            </MockRow>
          </Box>
          <Typography fontSize="14px" fontWeight={700}>
            {t("Jobs")}
          </Typography>
          <MockTogglePanel
            title={t("Read the worklist")}
            description={t(
              "Who is booked, so exams land on the right patient."
            )}
            isOn
            targetId="j-wl"
          >
            <Typography fontSize="12px">
              {`${t("Read every {{minutes}} min", { minutes: 30 })} · ${t("today's appointments")}`}
            </Typography>
            <MockMoreOptions />
          </MockTogglePanel>
          <MockTogglePanel
            title={t("Bring in their exams")}
            description={t(
              "Only for patients on the worklist. Nobody else's exams leave {{name}}.",
              { name: "FORUM" }
            )}
            isOn
            targetId="j-ex"
          >
            <ExamTypePicker
              options={["Oct", "VisualField"]}
              value={["Oct", "VisualField"]}
              onChange={noop}
            />
            <MockMoreOptions targetId="j-more" />
            <MockCheckbox
              label={t("Let {{name}} push exams instead", { name: "FORUM" })}
              checked={view.isOn("push")}
              targetId="j-push"
            />
          </MockTogglePanel>
          <MockTogglePanel
            title={t("Send RetinaLogik reports back")}
            description={t("Stored in {{name}} next to the original exam.", {
              name: "FORUM",
            })}
            isOn={view.isOn("rep")}
            targetId="j-rep"
          >
            <MockField
              label={t("Send reports of exams from")}
              required
              value="2026-10-19"
            />
          </MockTogglePanel>
          <Box
            {...guideTarget("remove")}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            gap="10px"
            flexWrap="wrap"
          >
            <Typography fontSize="12px">
              {t(
                "Removing the PAC server stops all its jobs. Exams already brought in stay."
              )}
            </Typography>
            <MockButton kind="delete" small>
              {t("Remove PAC server")}
            </MockButton>
          </Box>
        </MockDialog>
      )}
    </Walkthrough>
  );
};
